import { useParams, useSearchParams } from 'react-router-dom';
import { Box, Button, Grid2, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { isEqual } from 'lodash';

import PageContainer from 'components/v2/container/PageContainer';
import {
  getBookingInfoByOrderIdApi,
  getOrderLinesApi,
  getOrderTasksApi,
  getOrderWatchersApi,
  getTaskDetailsNoCacheApi,
  updateResponsibleAgentMutation,
  updateWatchersMutation,
} from 'api/orders/query';
import { contactPersonsColumn, orderLinesInCaseColumn } from 'components/v2/division/column/ordersColumn';
import { useLocalization } from 'localization';
import { IDatahubTask, isEsoftUser, OrderStatus, TaskCategory } from 'api';
import { MODAL_ID, MODALS_STORE } from 'constants/ui/modal';
import AddOrderLineModal from 'modules/v2/servicesmodal/order/AddOrderLineModal';
import { getCaseDetailApi, getOrdersInCaseApi } from 'api/cases/query';
import { useEditableMode } from 'hooks/general/useEditableMode';
import { InputSelectField } from 'components/v2/input/InputSelectField';
import { InputMultiSelectField } from 'components/v2/input/InputMultiSelectField';
import { useDepartmentMembers } from 'modules/branches/hooks';
import { LAYOUT_SPACING } from 'constants/ui/style';
import TextEditIcon from 'components/v2/icons/TextEditIcon';
import DataGridTable from 'components/v2/table/DataGridTable';
import { CurrencyCode } from 'constants/enums/general';
import { useEffect, useMemo, useState } from 'react';

const OrderDetailInCase = () => {
  const { t, f } = useLocalization();
  const { isEditMode, toggleEditMode } = useEditableMode();
  const { orderId, id } = useParams();
  const { toggleModal, isModalOpen } = MODALS_STORE.orders();
  const [searchParams] = useSearchParams();
  const orderIdFromSearchParams = searchParams.get('orderId');
  const selectedOrderId = orderId || orderIdFromSearchParams;
  const { data, isLoading } = getOrderLinesApi(selectedOrderId ?? '');
  const { data: caseDetail } = getCaseDetailApi(id ?? '');
  const { data: ordersInCase } = getOrdersInCaseApi(id ?? '');
  const { areDepartmentMembersLoading, departmentMembers } = useDepartmentMembers(caseDetail?.departmentId);
  const { data: orderWatchers } = getOrderWatchersApi(selectedOrderId ?? '');
  const { data: bookingInfo } = getBookingInfoByOrderIdApi(selectedOrderId ?? '');
  const { data: orderTasks } = getOrderTasksApi(selectedOrderId ?? '');
  const [detailedTextTasks, setDetailedTextTasks] = useState<IDatahubTask[]>([]);

  useEffect(() => {
    if (orderTasks && orderTasks.length > 0) {
      const textTypeTasks = orderTasks.filter(task => task.type === TaskCategory.Text);

      if (textTypeTasks.length > 0) {
        // there is no single endpoint to get details for batch of tasks,
        // so we need to call the endpoint for each task conditionally
        // and therefore cannot use useQuery hook
        Promise.all(textTypeTasks.map(task => getTaskDetailsNoCacheApi(task.id))).then(taskItems =>
          setDetailedTextTasks(taskItems.filter((taskItem): taskItem is IDatahubTask => !!taskItem))
        );
      }
    }
  }, [orderTasks]);

  const extendedOrderLines = useMemo(() => {
    return data?.map(orderLine => ({
      ...orderLine,
      // combined notes alltogether into a single string
      notes: detailedTextTasks
        .filter(task => task.orderlineId === orderLine.id)
        ?.map(task => task.notes?.map(note => note.text).join('\n'))
        .join('\n'),
    }));
  }, [data, detailedTextTasks]);

  const totalPrice =
    data?.filter(({ status }) => status !== OrderStatus.Cancelled).reduce((memo, next) => memo + next.price, 0) || 0;

  const selectedOrder = ordersInCase?.find(({ id }) => id === orderId);

  const departmentMemberOptions = departmentMembers.map(({ fullName, id }) => ({
    value: id,
    label: fullName,
  }));

  const defaultFormValue = {
    responsibleId: ordersInCase?.find(order => order?.id === orderId)?.responsibleUserId ?? '',
    watcherIds: orderWatchers?.map(watcher => watcher.id) ?? [],
  };

  const { control, reset, getValues, watch } = useForm({
    defaultValues: defaultFormValue,
    mode: 'onChange',
    values: defaultFormValue,
  });

  const handleUpdateSuccess = () => {
    reset();
    toggleEditMode();
  };

  const { updateResponsibleAgentAsync, isUpdatingResponsibleAgent } = updateResponsibleAgentMutation({
    orderId: orderId ?? '',
    responsibleAgentId: watch('responsibleId'),
    caseId: id,
    handleSuccess: handleUpdateSuccess,
  });

  const { updateWatchersAsync, isUpdatingWatchers } = updateWatchersMutation({
    orderId: orderId ?? '',
    watcherIds: watch('watcherIds'),
    handleSuccess: handleUpdateSuccess,
  });

  const handleUpdateInformation = async () => {
    if (defaultFormValue.responsibleId !== getValues('responsibleId')) {
      await updateResponsibleAgentAsync();
    }
    if (!isEqual(defaultFormValue.watcherIds, getValues('watcherIds'))) {
      await updateWatchersAsync();
    }
  };

  return (
    <PageContainer pageTitle="orders.title.orderInformation">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h4"> {t('orders.title.orderLine')}</Typography>
        {/* DO NOT DELETE */}
        {/* {isEsoftUser() && (
          <Button
            variant="contained"
            size={'medium'}
            startIcon={<PlusIcon />}
            onClick={() => toggleModal(MODAL_ID.ADD_ORDER_LINE, { orderId: orderId, clientId: caseDetail?.clientId })}
          >
            {t('common.terms.addExtraOrderLine')}
          </Button>
        )} */}
      </Stack>
      <Box sx={{ margin: '1rem 0' }}>
        <DataGridTable
          rows={extendedOrderLines ?? []}
          columns={orderLinesInCaseColumn() as any}
          loading={isLoading}
          slots={{
            footer: () => (
              <Stack justifyContent="flex-end" padding={2} gap={2} direction="row">
                <Typography fontSize="1.2rem">{t('common.terms.totalPrice')}:</Typography>
                <Typography fontSize="1.2rem">
                  {f(totalPrice, { currency: selectedOrder?.currency || CurrencyCode.DKK, decimals: 2 })}
                </Typography>
              </Stack>
            ),
          }}
          sx={{ minHeight: '12rem' }}
        />
      </Box>

      <Stack spacing={LAYOUT_SPACING.FORM.md} sx={{ marginTop: 2 }}>
        <Typography variant="h4"> {t('orders.pages.details.informationCardTitle')}</Typography>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Grid2 container spacing={LAYOUT_SPACING.FORM.sm} sx={{ flex: 1 }}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <InputSelectField
                name="responsibleId"
                control={control}
                options={departmentMemberOptions}
                label="common.terms.responsibleAgent"
                menuHeight={300}
                required
                loading={areDepartmentMembersLoading}
                disabled={!isEditMode}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <InputMultiSelectField
                name="watcherIds"
                control={control}
                options={departmentMemberOptions}
                label="common.terms.watchers"
                menuHeight={600}
                loading={areDepartmentMembersLoading}
                required
                disabled={!isEditMode}
              />
            </Grid2>
          </Grid2>
          <Button
            onClick={toggleEditMode}
            variant="outlined"
            endIcon={<TextEditIcon />}
            sx={{
              marginBottom: '-1.2rem !important',
              display: isEditMode ? 'none' : 'flex',
              whiteSpace: 'nowrap',
            }}
            size="medium"
          >
            {t('cases.cloudScanner.edit')}
          </Button>
          {isEditMode && (
            <Stack direction="row" justifyContent="center" alignItems="center" gap={2}>
              <Button
                variant="outlined"
                sx={{ marginBottom: '-1.2rem !important' }}
                size="medium"
                onClick={() => {
                  toggleEditMode();
                  reset();
                }}
                disabled={isUpdatingResponsibleAgent || isUpdatingWatchers}
              >
                {t('common.actions.cancel')}
              </Button>
              <Button
                variant="contained"
                sx={{ marginBottom: '-1.2rem !important' }}
                size="medium"
                onClick={handleUpdateInformation}
                loading={isUpdatingResponsibleAgent || isUpdatingWatchers}
              >
                {t('common.actions.confirm')}
              </Button>
            </Stack>
          )}
        </Stack>
        <Stack spacing={2}>
          <Typography variant="h4"> {t('orders.pages.details.booking')}</Typography>
          <Typography>
            {t('orders.pages.details.dateOfPhotography')}:{' '}
            <Typography variant="body2">
              {bookingInfo?.bookingStartsAt
                ? `${f(bookingInfo?.bookingStartsAt, { date: 'short', time: 'short' })}${
                    bookingInfo?.bookingEndsAt ? ` - ${f(bookingInfo?.bookingEndsAt, { time: 'short' })}` : ''
                  }`
                : '-'}
            </Typography>
          </Typography>
          <Typography>
            {t('common.terms.photographer')}:
            <Typography variant="body2">{bookingInfo?.photographerName ?? '-'}</Typography>
          </Typography>
          <Typography>
            {t('orders.pages.details.informationForPhotographer')}:{' '}
            <Typography variant="body2">
              <div dangerouslySetInnerHTML={{ __html: bookingInfo?.comments ?? '' }} />
            </Typography>
          </Typography>
          <Typography variant="h4"> {t('orders.pages.details.contactPersons')}</Typography>
          <DataGridTable
            rows={(caseDetail?.contactPersons ?? []).map((person, idx) => ({
              id: idx,
              ...person,
            }))}
            columns={contactPersonsColumn()}
            loading={isLoading}
            isHideFooter
            sx={{ minHeight: '8rem' }}
          />
        </Stack>
      </Stack>
      {isModalOpen(MODAL_ID.ADD_ORDER_LINE) && <AddOrderLineModal />}
    </PageContainer>
  );
};

export default OrderDetailInCase;
